import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { COMPANY, IMAGES } from "../lib/constants";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Installazione impianto fotovoltaico",
  "Assistenza e manutenzione",
  "Lavaggio pannelli",
  "Ristrutturazione edile",
  "Altro / Consulenza",
];

export default function Contatti() {
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    email: "",
    servizio: SERVICE_OPTIONS[0],
    messaggio: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const updateField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.nome.trim() ||
      !form.telefono.trim() ||
      !form.email.trim() ||
      !form.messaggio.trim()
    ) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact-requests`, form);
      toast.success("Richiesta inviata! Ti contatteremo al più presto.");
      setSuccess(true);
      setForm({
        nome: "",
        telefono: "",
        email: "",
        servizio: SERVICE_OPTIONS[0],
        messaggio: "",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore nell'invio. Riprova o chiamaci.";
      toast.error(typeof msg === "string" ? msg : "Errore nell'invio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contatti">
      <SEO
        title="Contatti — Richiedi un Preventivo Gratuito"
        description="Contatta Impresa Edile Lici per impianti fotovoltaici, manutenzione e lavaggio pannelli. Tel +39 351 589 7637 · Email ftvlici@gmail.com · Seveso (MB)."
        path="/contatti"
      />
      {/* HERO */}
      <section className="relative h-[40vh] min-h-[320px] flex items-end overflow-hidden">
        <img src={IMAGES.rooftopWorker} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-14 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="accent-line" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#FED7AA]">Contatti</span>
          </div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-tight max-w-3xl">
            Parliamone. <span className="italic text-[#FED7AA]">Senza impegno.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* INFO COLUMN */}
          <aside className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="accent-line" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#D97706] font-medium">
                Resta in contatto
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0F5132] leading-tight">
              Massimizza l'efficienza del tuo impianto.
            </h2>
            <p className="mt-5 text-[#4B5563] leading-relaxed">
              Hai bisogno di un preventivo per una nuova installazione o vuoi prenotare un lavaggio
              dei pannelli? Il nostro team è a tua disposizione per trovare la soluzione su misura
              per te.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-phone-link"
                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-[#E5E7EB] hover:border-[#0F5132] hover:shadow-md transition-all group"
              >
                <div className="h-11 w-11 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center group-hover:bg-[#0F5132] group-hover:text-white transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">Telefono</div>
                  <div className="font-serif text-lg text-[#0F5132]">{COMPANY.phone}</div>
                </div>
              </a>
              <a
                href={COMPANY.emailHref}
                data-testid="contact-email-link"
                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-[#E5E7EB] hover:border-[#0F5132] hover:shadow-md transition-all group"
              >
                <div className="h-11 w-11 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center group-hover:bg-[#0F5132] group-hover:text-white transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">Email</div>
                  <div className="font-serif text-lg text-[#0F5132] break-all">{COMPANY.email}</div>
                </div>
              </a>
              <a
                href={COMPANY.addressMapUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-address-link"
                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-[#E5E7EB] hover:border-[#0F5132] hover:shadow-md transition-all group"
              >
                <div className="h-11 w-11 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center group-hover:bg-[#0F5132] group-hover:text-white transition-colors">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">Sede</div>
                  <div className="font-serif text-lg text-[#0F5132]">{COMPANY.address}</div>
                </div>
              </a>
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-[#E5E7EB]">
                <div className="h-11 w-11 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">Orari</div>
                  <div className="font-serif text-base text-[#0F5132]">Lun – Ven 08:00 – 18:00</div>
                  <div className="text-sm text-[#4B5563]">Sabato su appuntamento</div>
                </div>
              </div>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-7 md:p-10">
              <h3 className="font-serif text-2xl md:text-3xl text-[#0F5132]">
                Richiedi un preventivo gratuito
              </h3>
              <p className="mt-2 text-sm text-[#4B5563]">
                Compila il modulo, ti risponderemo entro 24 ore lavorative.
              </p>

              {success && (
                <div
                  data-testid="success-message"
                  className="mt-6 p-4 rounded-xl bg-[#0F5132]/10 border border-[#0F5132]/20 text-[#0F5132] text-sm"
                >
                  Richiesta ricevuta correttamente. Ti contatteremo al più presto.
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                <div className="md:col-span-1">
                  <label className="block text-xs uppercase tracking-widest text-[#4B5563] mb-2">
                    Nome e Cognome*
                  </label>
                  <input
                    data-testid="input-nome"
                    type="text"
                    value={form.nome}
                    onChange={updateField("nome")}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#0F5132] focus:border-[#0F5132] transition"
                    placeholder="Mario Rossi"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs uppercase tracking-widest text-[#4B5563] mb-2">
                    Telefono*
                  </label>
                  <input
                    data-testid="input-telefono"
                    type="tel"
                    value={form.telefono}
                    onChange={updateField("telefono")}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#0F5132] focus:border-[#0F5132] transition"
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-[#4B5563] mb-2">
                    Email*
                  </label>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={updateField("email")}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#0F5132] focus:border-[#0F5132] transition"
                    placeholder="nome@email.it"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-[#4B5563] mb-2">
                    Servizio di interesse
                  </label>
                  <select
                    data-testid="select-servizio"
                    value={form.servizio}
                    onChange={updateField("servizio")}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#0F5132] focus:border-[#0F5132] transition"
                  >
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-[#4B5563] mb-2">
                    Messaggio*
                  </label>
                  <textarea
                    data-testid="input-messaggio"
                    value={form.messaggio}
                    onChange={updateField("messaggio")}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#0F5132] focus:border-[#0F5132] transition resize-none"
                    placeholder="Raccontaci di cosa hai bisogno (tipo di edificio, mq di tetto, consumo annuo, problemi attuali…)"
                  />
                </div>
                <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap mt-2">
                  <p className="text-xs text-[#4B5563]">
                    Inviando il modulo accetti il trattamento dei dati ai sensi del GDPR.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-contact-button"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0F5132] text-white font-medium hover:bg-[#0A3622] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Invio in corso…" : "Invia richiesta"}
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
